import { useState, useMemo, useRef, useEffect, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Search } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { extractErr } from '@/components/ui/inline-edit'

type Person = {
  id: number
  full_name: string
  personal_email: string
  current_title: string
}

type Profile = {
  id: number
  person: number
  person_name: string
  anonymous_code: string
  profile_summary: string
  total_years_experience: number
  industries: string[]
  highest_education_level: string
  is_active: boolean
  skill_count: number
  /** Nested via ConsultantProfileSerializer.skills — used by the client-side search. */
  skills?: { skill_name: string }[]
  /** ATS candidate-profile id when the Person also has an ATS sidecar (null otherwise). */
  candidate_profile_id: number | null
}

export default function ConsultingConsultants() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const profilesQ = useQuery({
    queryKey: ['consulting', 'profiles'],
    queryFn: async () => {
      const r = await api.get('/consulting/profiles/')
      const body = r.data
      if (Array.isArray(body)) return body as Profile[]
      if (body && Array.isArray(body.results)) return body.results as Profile[]
      return [] as Profile[]
    },
  })

  const peopleQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => {
      const r = await api.get('/hr/persons/')
      const body = r.data
      if (Array.isArray(body)) return body as Person[]
      if (body && Array.isArray(body.results)) return body.results as Person[]
      return [] as Person[]
    },
  })

  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (payload: { person: number }) =>
      (await api.post('/consulting/profiles/', payload)).data as Profile,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profiles'] })
      setCreating(false)
      toast.success(`Created profile ${p.anonymous_code}`)
      navigate(`/admin/consulting/consultants/${p.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const profiles = profilesQ.data || []
  const people = peopleQ.data || []
  const personIdsWithProfile = new Set(profiles.map((p) => p.person))
  const availablePeople = people.filter((p) => !personIdsWithProfile.has(p.id))

  const [search, setSearch] = useState('')
  const filteredProfiles = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return profiles
    return profiles.filter((p) => {
      if (p.anonymous_code.toLowerCase().includes(q)) return true
      if (p.person_name.toLowerCase().includes(q)) return true
      if (p.skills && p.skills.some((s) => s.skill_name.toLowerCase().includes(q))) return true
      return false
    })
  }, [profiles, search])

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground hover:underline">← Dashboard</Link>
          </div>
          <h2 className="text-2xl font-bold text-foreground mt-1">
            Profiles ({profiles.length})
          </h2>
          <p className="text-sm text-muted-foreground">
            Anonymized consultant profiles used to present to clients.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ New profile</Button>
      </div>

      {profilesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : profiles.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-2">No consultant profiles yet.</p>
          <p className="text-sm text-muted-foreground">
            Click "New profile" to anonymize a person for client presentation.
          </p>
        </div>
      ) : (
        <>
        <div className="mb-3 relative max-w-md">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, name, or skill…"
            className="pl-9"
          />
        </div>
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground bg-muted/30 border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Code</th>
                <th className="text-left px-4 py-2 font-medium">Person (internal)</th>
                <th className="text-right px-4 py-2 font-medium">Years</th>
                <th className="text-right px-4 py-2 font-medium">Skills</th>
                <th className="text-center px-4 py-2 font-medium">Active</th>
                <th className="text-left px-4 py-2 font-medium">Candidate</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground italic">
                    No profiles match "{search}".
                  </td>
                </tr>
              ) : (
                filteredProfiles.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/admin/consulting/consultants/${p.id}`)}
                  >
                    <td className="px-4 py-3 font-mono font-medium">{p.anonymous_code}</td>
                    <td className="px-4 py-3">{p.person_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.total_years_experience}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.skill_count}</td>
                    <td className="px-4 py-3 text-center">
                      {p.is_active ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Active</span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {p.candidate_profile_id ? (
                        <Link
                          to={`/admin/ats/candidates/${p.candidate_profile_id}`}
                          className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1"
                          title="Open candidate profile in ATS"
                        >
                          ATS profile →
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {creating && (
        <CreateDialog
          people={availablePeople}
          submitting={createMut.isPending}
          onCancel={() => setCreating(false)}
          onSubmit={(personId) => createMut.mutate({ person: personId })}
        />
      )}
    </AdminLayout>
  )
}

function CreateDialog({
  people, onSubmit, onCancel, submitting,
}: {
  people: Person[]
  onSubmit: (personId: number) => void
  onCancel: () => void
  submitting: boolean
}) {
  const [personId, setPersonId] = useState<number | null>(null)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!personId) return
    onSubmit(personId)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-1">New profile</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Pick the Person to anonymize. A consultant code (C-NNNNN) is allocated automatically.
        </p>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="person">Person</FieldLabel>
                <SearchablePersonPicker
                  id="person"
                  people={people}
                  value={personId}
                  onChange={setPersonId}
                />
              </Field>
            </FieldGroup>
          </FieldSet>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={!personId || submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}


/**
 * Self-contained searchable dropdown for picking a Person. Built without
 * Radix Popover/Select so it works reliably inside custom modals and
 * doesn't hit the pointer-events trap that bites Radix-portalled content.
 */
function SearchablePersonPicker({
  id, people, value, onChange,
}: {
  id?: string
  people: Person[]
  value: number | null
  onChange: (id: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Outside-click and Escape close the dropdown (same pattern as DatePicker)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const w = wrapperRef.current
      if (w && !w.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    // Focus the input as soon as we open
    setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const selected = value ? people.find((p) => p.id === value) || null : null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return people
    return people.filter((p) =>
      p.full_name.toLowerCase().includes(q) ||
      (p.current_title || '').toLowerCase().includes(q) ||
      (p.personal_email || '').toLowerCase().includes(q),
    )
  }, [people, query])

  const empty = people.length === 0

  return (
    <div ref={wrapperRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={empty}
        onClick={() => setOpen((o) => !o)}
        className={`w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 h-10 text-sm text-left ${
          empty ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted/30'
        }`}
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {empty
            ? 'No people available'
            : selected
              ? `${selected.full_name}${selected.current_title ? ' — ' + selected.current_title : ''}`
              : 'Pick a person…'}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>
      {open && !empty && (
        <div className="absolute z-[60] mt-1 left-0 right-0 rounded-lg bg-popover shadow-md ring-1 ring-foreground/10">
          <div className="p-2 border-b">
            <Input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, title, or email…"
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground italic">No matches.</div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(p.id); setOpen(false); setQuery('') }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${
                    p.id === value ? 'bg-muted/30' : ''
                  }`}
                >
                  <div className="font-medium text-foreground">{p.full_name}</div>
                  {p.current_title && (
                    <div className="text-xs text-muted-foreground">{p.current_title}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
